/**
 * Document Store — API-backed persistence
 *
 * This module is the single source of truth for document I/O.
 * All functions talk to the Express mock server via httpClient.
 *
 * To migrate to a real cloud backend, only the BASE_URL in httpClient
 * needs to change — nothing here or in the callers needs to change.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DocumentStub {
  id: string;
  title: string;
  updatedAt: string;
  framework: string;
  ownerId?: string;
}

export interface DocumentMetadata {
  documentId: string | null;
  title: string;
  author: string;
  framework: string;
  createdAt: string;
  updatedAt: string;
}

export interface SavedDocument {
  id: string;
  metadata?: DocumentMetadata;
  ownerId?: string;
  [key: string]: any;
}

// ─── Storage Key Helpers ──────────────────────────────────────────────────────
const INDEX_KEY = 'rb_doc_index';
const docKey = (id: string) => `rb_doc_${id}`;
const readIndex = (): DocumentStub[] => {
  try {
    const raw = localStorage.getItem(INDEX_KEY);

    return raw ? (JSON.parse(raw) as DocumentStub[]) : [];
  } catch {
    return [];
  }
};
const writeIndex = (index: DocumentStub[]): void => {
  localStorage.setItem(INDEX_KEY, JSON.stringify(index));
};

// ─── API Functions ────────────────────────────────────────────────────────────

/**
 * Returns document stubs (no full content) sorted newest-first.
 * Real backend: GET /api/documents
 */
export const getDocuments = async (): Promise<DocumentStub[]> => {
  try {
    const index = readIndex();

    return await Promise.resolve(
      [...index].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
    );
  } catch (err) {
    console.error('[documentStore] Failed to fetch documents:', err);

    return await Promise.resolve([]);
  }
};

/**
 * Loads a single document by ID.
 * Real backend: GET /api/documents/:id
 */
export const loadDocument = async (
  id: string
): Promise<SavedDocument | null> => {
  try {
    const raw = localStorage.getItem(docKey(id));
    if (!raw) return await Promise.resolve(null);

    return await Promise.resolve(JSON.parse(raw) as SavedDocument);
  } catch (err: any) {
    if (err?.response?.status === 404) return await Promise.resolve(null);
    console.error('[documentStore] Failed to load document:', err);

    return await Promise.resolve(null);
  }
};

/**
 * Saves (upserts) a document.
 * Real backend: POST /api/documents/:id
 * @returns updatedAt timestamp from the server
 */
export const saveDocument = async (
  id: string,
  document: any
): Promise<string | null> => {
  try {
    const updatedAt = new Date().toISOString();
    // Write full document
    const toSave: SavedDocument = {...document, id, updatedAt};
    localStorage.setItem(docKey(id), JSON.stringify(toSave));
    // Update or insert stub in index
    const index = readIndex();
    const stub: DocumentStub = {
      id,
      title:
        document?.metadata?.title || document?.title || 'Untitled Document',
      framework:
        document?.metadata?.framework || document?.framework || 'blank',
      updatedAt
    };
    const existingIdx = index.findIndex((s) => s.id === id);
    if (existingIdx >= 0) {
      index[existingIdx] = stub;
    } else {
      index.push(stub);
    }
    writeIndex(index);
    loadDocument(id)
      .then(() => {})
      .catch(() => {});

    return await Promise.resolve(updatedAt);
  } catch (err) {
    console.error('[documentStore] Failed to save document:', err);

    return await Promise.resolve(null);
  }
};

/**
 * Deletes a document.
 * Real backend: DELETE /api/documents/:id
 */
export const deleteDocument = async (id: string): Promise<boolean> => {
  try {
    localStorage.removeItem(docKey(id));
    const index = readIndex().filter((s) => s.id !== id);
    writeIndex(index);

    return await Promise.resolve(true);
  } catch (err) {
    console.error('[documentStore] Failed to delete document:', err);

    return await Promise.resolve(false);
  }
};
